import { useEffect } from "react";

import { UxFlavor } from "slices/explorer/types";
import { isIpad } from "utils/misc";

/**
 * Overrides the container's 100dvh/100dvw with stable window.innerHeight/innerWidth
 * pixel values in environments where the dynamic viewport is unreliable:
 *
 * 1. iPad Chrome: viewport height is wrong on initial page load from Projects
 *    (100dvh is taller than window.innerHeight).
 *    @see https://stackoverflow.com/questions/69769235/chrome-ipad-has-wrong-viewport-height
 *
 * 2. iPad WKWebView (e.g. Procore iOS app): after orientation change with a
 *    side panel open, 100dvh oscillates between two values as iOS recalculates
 *    the dynamic viewport in response to scrollable panel content, causing the
 *    UI to continuously snap between full screen and ~95% fill.
 *
 * 3. Android Chrome / Trusted Web Activity:
 *    similar dynamic viewport instability when rotating the device, which can
 *    yield transient zero-size layout and WebGL crash in the viewer.
 */
export function useStableViewportFix(uxFlavor?: UxFlavor) {
    useEffect(() => {
        const useStableViewport = isIpad || uxFlavor === UxFlavor.ProcoreAndroid || uxFlavor === UxFlavor.ProcoreIOS;

        if (!useStableViewport) {
            return;
        }

        function onResize() {
            const container = document.getElementById("container");
            const isContainerChildOfBody = container?.parentElement?.tagName === "BODY";
            if (container && isContainerChildOfBody) {
                container.style.height = window.innerHeight + "px";
                container.style.width = window.innerWidth + "px";
            }
        }
        onResize();
        window.addEventListener("resize", onResize);
        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, [uxFlavor]);
}
